import {Schema, SearchableProperty} from '../types/Schema';
import * as YamlUtils from './yaml-utils';
import * as RbacUtils from './rbac-utils';
import {SchemaInfo} from '../types/SchemaInfo';

// From SOaaS documentation
const DEFAULT_PROPERTY_RANGE = 'string';
const SCHEMA_ID = '@id';
export const SCHEMA_TYPE = '@type';
export const SCHEMA_HYDRA_MEMBER = "hydra:member";
export const SCHEMA_BODY = 'input';
export const SCHEMA_BOUND = 'bound';
export const GENERATION_MESSAGES = 'messages';

export const DEFAULT_ANALYZER = 'standard';

export const FIELD_TO_TITLE_MAP = {
  'id': 'ID',
  'type': 'Type',
  'label': 'Label',
  'creator': 'Creator',
  'versionInfo': 'Version',
  'created': 'Created',
  'updated': 'Updated',
  'bound': 'Active',
  'objectsCount': 'Objects',
  'propertiesCount': 'Properties'
};

const LITERAL_TYPES = [
  'int',
  'double',
  'string',
  'boolean',
  'iri',
  'long',
  'short',
  'byte',
  'unsignedLong',
  'unsignedInt',
  'unsignedShort',
  'unsignedByte',
  'decimal',
  'integer',
  'positiveInteger',
  'nonPositiveInteger',
  'negativeInteger',
  'nonNegativeInteger',
  'dateTime',
  'time',
  'date',
  'year',
  'yearMonth',
  'langString',
  'stringOrLangString',
  'dateOrYearOrMonth',
];

/**
 * Parses incoming schema as ld+json to a Schema.
 *
 * @param schema the SOML schema to parse
 * @returns {Schema} the parsed schema
 */
export function parseSchemaJsonLd(schema: any): Schema {
  const schemaBody = schema[SCHEMA_BODY];
  const schemaYaml: any = YamlUtils.parseYaml(schemaBody);
  const info = getSchemaInfo(schemaYaml);
  const rawId = schema[SCHEMA_ID];
  const schemaId = resolveSchemaId(rawId);
  const bound = schema[SCHEMA_BOUND];
  const defaultAnalyzer = schemaYaml.config?.search?.analysis?.analyzer || DEFAULT_ANALYZER;
  const searchableProperties = extractSearchableProperties(schemaYaml, defaultAnalyzer);
  return {
    id: schemaId,
    rawId,
    bound,
    schema: schemaBody,
    schemaYaml,
    properties: {
      id: schemaId,
      type: schema[SCHEMA_TYPE],
      label: schemaYaml.label,
      creator: schemaYaml.creator,
      versionInfo: schemaYaml.versionInfo,
      created: (schemaYaml.created as Date),
      updated: (schemaYaml.updated as Date),
      bound,
      objectsCount: info.objects.total,
      propertiesCount: info.properties.total
    },
    info,
    permissions: RbacUtils.getPermissions(schemaId),
    messages: {
      warnings: schema.messages
    },
    defaultAnalyzer,
    searchableProperties,
  };
}

/**
 * Same as {@link #parseSchemaJsonLd} but works with SOML as a string.
 *
 * Note: assumes the schema is not bound as there is no info about that.
 *
 * @param {string} schema the SOML schema to parse
 * @returns {Schema} the parsed schema
 */
export function parseSchemaYaml(schema: string): Schema {
  const schemaYaml: any = YamlUtils.parseYaml(schema);
  const info = getSchemaInfo(schemaYaml);
  const schemaId = resolveSchemaId(schemaYaml.id);
  const bound = false;
  const defaultAnalyzer = schemaYaml.config?.search?.analysis?.analyzer || DEFAULT_ANALYZER;
  const searchableProperties = extractSearchableProperties(schemaYaml, defaultAnalyzer);
  return {
    id: schemaId,
    rawId: schemaYaml.id,
    bound,
    schema: schema,
    schemaYaml,
    properties: {
      id: schemaId,
      type: 'SOML',
      label: schemaYaml.label,
      creator: schemaYaml.creator,
      versionInfo: schemaYaml.versionInfo,
      created: (schemaYaml.created as Date),
      updated: (schemaYaml.updated as Date),
      bound,
      objectsCount: info.objects.total,
      propertiesCount: info.properties.total
    },
    info,
    permissions: RbacUtils.getPermissions(schemaId),
    defaultAnalyzer,
    searchableProperties
  };
}

export function getSchemaInfo(schemaYaml: any): SchemaInfo {
  const info = {
    objects: {
      total: 0,
      concrete: 0,
      interface: 0
    },
    properties: {
      total: 0,
      dataProperty: 0,
      objectProperty: 0
    },
    roles: {
      total: 0
    }
  };
  if (schemaYaml.rbac && schemaYaml.rbac.roles) {
    info.roles.total = Object.keys(schemaYaml.rbac.roles).length;
  }
  const objects = schemaYaml.objects;
  if (objects) {
    info.objects.total = (Object.keys(objects).length) || 0;
    Object.keys(objects).forEach((name) => {
      const object = objects[name];
      if (object.kind === 'abstract') {
        info.objects.interface++;
      } else {
        info.objects.concrete++;
      }
      if (object.props) {
        countPropertiesByType(object.props, info);
      }
    });
  }
  if (schemaYaml.properties) {
    countPropertiesByType(schemaYaml.properties, info);
  }
  info.properties.total = info.properties.dataProperty + info.properties.objectProperty;
  return info;
}

function countPropertiesByType(properties: any, info: SchemaInfo) {
  Object.keys(properties).forEach(propName => {
    const property = properties[propName];
    // property name without value is considered a string type data property
    if (!property || !property.range || LITERAL_TYPES.indexOf(property.range) !== -1) {
      info.properties.dataProperty++;
    } else {
      info.properties.objectProperty++;
    }
  });
}

/**
 * Resolves SOML schema reference ID. For example /soml/swapi becomes just swapi.
 *
 * @param {string} id the ID to resolve
 * @returns {string} the resolved ID
 */
export function resolveSchemaId(id: string) {
  if (id) {
    const position = id.lastIndexOf('/');
    if (position > 0) {
      return id.substring(position + 1);
    }
    // return the id as is
  }
  return id;
}

/**
 * Parse provided schema string and try to extract the schema id from it.
 * @param {string} content The schema as string.
 * @return {string}
 */
export function extractSchemaId(content: string): string {
  const schemaYaml: any = content && YamlUtils.parseYaml(content);
  const id = schemaYaml && resolveSchemaId(schemaYaml.id);
  return id || '';
}

export function extractSchemaIdAsync(content: string): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const schemaId = extractSchemaId(content);
      resolve(schemaId);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Extracts an object name to searchable properties map from schema
 *
 * @param schemaYaml the SOML schema to search
 * @param defaultAnalyzer default schema analyzer
 *
 * @return Map<string, Array<SearchableProperty>> Object name to searchable properties array
 */
const extractSearchableProperties = (schemaYaml: any, defaultAnalyzer: any): Map<string, Array<SearchableProperty>> => {
  if (!schemaYaml.objects) {
    return new Map();
  }
  const searchablePropertiesMap: Map<string, Array<SearchableProperty>> = new Map<string, Array<SearchableProperty>>();
  Object.keys(schemaYaml.objects)
    .forEach((objectKey) => {
      const searchableProperties = getSearchableProperties(objectKey, defaultAnalyzer, schemaYaml);
      if (searchableProperties) {
        searchablePropertiesMap.set(objectKey, searchableProperties);
      }
    });
  return searchablePropertiesMap;
};

const getSearchableProperties = (objectKey: string, defaultAnalyzer: any, schemaYaml: any): Array<SearchableProperty> | undefined => {
  const props = getSearchableObjectProperties(objectKey, schemaYaml);
  if (!props) {
    return undefined;
  }
  const searchableProperties: Array<SearchableProperty> = [];
  Object.keys(props)
    .forEach(propKey => {
      const property = props[propKey];
      const schemaProps = schemaYaml.properties || {};
      searchableProperties.push({
        name: propKey,
        gqlName: propKey.replace(':', '_'),
        analyzer: property?.search?.analysis?.analyzer || schemaProps[propKey]?.search?.analysis?.analyzer || defaultAnalyzer,
        range: property?.range || schemaProps[propKey]?.range || DEFAULT_PROPERTY_RANGE,
        searchType: property?.search?.type || schemaProps[propKey]?.search?.type
      });
    });
  return searchableProperties;
};

const getSearchableObjectProperties = (objectKey: any, schemaYaml: any): any => {
  const object = schemaYaml.objects[objectKey];
  const properties = object.props;
  let objectProps: { [key: string]: any } = {};

  if (object.search?.index === 'yes') {
    if (properties) {
      Object.keys(properties)
        .forEach(propKey => {
          if (properties[propKey] && properties[propKey].search?.index !== 'no') {
            objectProps[propKey] = properties[propKey];
          }
          if (!properties[propKey] && schemaYaml.properties[propKey].search?.index !== 'no') {
            objectProps[propKey] = schemaYaml.properties[propKey];
          }
        });
    }
    if (object.inherits) {
      const inheritedProps = getSearchableObjectProperties(object.inherits, schemaYaml);
      objectProps = {...objectProps, ...inheritedProps};
    }
    return objectProps;
  }
  return undefined;
};
